import React from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import useCopyToClipboard from "../hooks/useCopyToClipboard";
import MenuNotPublishedPlaceholder from "./MenuNotPublishedPlaceholder";
import { MenuTypeKey, menuTypes } from "../constants";
import { downloadQRCode } from "@me-team/host/src/utils/utils";
import { getDownloadableQrUrl } from "../helpers";
import { cloneNameToDisplay } from "../../utils/cloneNameUtils";
import { PartialMenu } from "../../../../interfaces/meMenuInterfaces/interfaces";

interface ActiveMenusByTypeProps {
    menus: Record<MenuTypeKey, PartialMenu | null>;
    onHideMenu?: (menu: PartialMenu) => void;
    onChangeMenu?: (type: MenuTypeKey) => void;
    canManageActiveMenus?: boolean;
}

const ActiveMenusByType: React.FC<ActiveMenusByTypeProps> = ({
    menus,
    onHideMenu,
    onChangeMenu,
    canManageActiveMenus = false,
}) => {
    const { t } = useTranslation();
    const { copy, copiedText } = useCopyToClipboard();
    const basePath = process.env.REACT_APP_POS_URL;

    return (
        <>
            <>
                <Col xxl={8} className="mb-3">
                    <h3 className="fs-5 text-dark fw-bold mb-2">{t("Menus are available via QR code")}</h3>
                    <p className="text-grey">
                        {t(
                            "Select the menus that will be accessible to customers via QR codes and links. The QR codes and links remain unchanged. You can switch the active menu without reprinting."
                        )}
                    </p>
                </Col>

                <Row className="gap-3 gap-xl-0 mb-8">
                    {menuTypes.map((type) => {
                        const menu = menus?.[type.key as MenuTypeKey];
                        return (
                            <Col xl={4} key={type.key}>
                                {menu ? (
                                    <div className="p-3 border rounded-1">
                                        <Row className="gx-3">
                                            <Col xs={3} className="d-flex align-items-center">
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={<Tooltip>{t("Download QR code")}</Tooltip>}
                                                >
                                                    <img
                                                        src={menu.qrMenuQrCode}
                                                        width={78}
                                                        height={78}
                                                        style={{ maxWidth: "90px", maxHeight: "90px" }}
                                                        className="object-fit-contain w-100 h-100 cursor-pointer"
                                                        alt={cloneNameToDisplay(menu.name, t)}
                                                        onClick={() =>
                                                            downloadQRCode(
                                                                getDownloadableQrUrl(menu.qrMenuQrCode, basePath)
                                                            )
                                                        }
                                                    />
                                                </OverlayTrigger>
                                            </Col>

                                            <Col xs={7} className="d-flex flex-column min-w-0">
                                                <div>
                                                    <Badge
                                                        bg="violet-light"
                                                        className="d-inline-block fs-7 lh-sm text-primary rounded-1 py-1 px-2 mb-1"
                                                    >
                                                        {t(type.label)}
                                                    </Badge>
                                                </div>

                                                <p className="fw-bold text-truncate lh-22 mb-2">
                                                    {cloneNameToDisplay(menu.name, t)}
                                                </p>

                                                <div className="d-flex align-items-center min-w-0">
                                                    <a
                                                        href={menu.qrMenuLink}
                                                        className="flex-grow-1 text-truncate d-block fs-7 lh-sm text-primary me-2"
                                                        title={menu.qrMenuLink}
                                                    >
                                                        {menu.qrMenuLink}
                                                    </a>

                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip>
                                                                {copiedText === menu.qrMenuLink
                                                                    ? t("Copied")
                                                                    : t("Copy")}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <Button
                                                            variant="link"
                                                            className="text-primary p-0 flex-shrink-0"
                                                            onClick={() => copy(menu.qrMenuLink)}
                                                        >
                                                            <i
                                                                className={
                                                                    copiedText === menu.qrMenuLink
                                                                        ? "bi bi-check"
                                                                        : "bi bi-copy"
                                                                }
                                                            />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </Col>

                                            {canManageActiveMenus ? (
                                                <Col
                                                    xs={2}
                                                    className="d-flex flex-column align-items-end justify-content-between"
                                                >
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={<Tooltip>{t("Hide menu")}</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-secondary"
                                                            className="btn-size-qr p-1 mb-2"
                                                            onClick={() => onHideMenu?.(menu)}
                                                        >
                                                            <i className="bi bi-eye-slash fs-6 text-dark"></i>
                                                        </Button>
                                                    </OverlayTrigger>

                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={<Tooltip>{t("Change menu")}</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-secondary"
                                                            className="btn-size-qr p-1"
                                                            onClick={() => onChangeMenu?.(type.key as MenuTypeKey)}
                                                        >
                                                            <i className="bi bi-arrow-repeat fs-6 text-dark"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                </Col>
                                            ) : null}
                                        </Row>
                                    </div>
                                ) : (
                                    <MenuNotPublishedPlaceholder badgeText={type.label} />
                                )}
                            </Col>
                        );
                    })}
                </Row>
            </>
        </>
    );
};

export default ActiveMenusByType;
